import { useMemo, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { SortingState, Table, Updater } from '@tanstack/react-table';

import { DataTable } from '../DataTable';
import { MouseTooltip } from 'components/ui/MouseTooltip';
import type {
  YardGateTrackedStepColumn,
  YardGateMutableStatus,
  YardGateDockOption,
} from 'contracts/YardGate';
import { YardGateEntry } from '../../adapter';
import { buildYardGateColumns } from './columns';
import { Colors } from 'styles/constants';
import type { Pagination } from 'interfaces/pagination';

export const YARD_GATE_DEFAULT_COLUMN_PINNING = {
  left: ['Status', 'Ord. Coleta'],
};

interface EntriesTableProps {
  entries: YardGateEntry[];
  trackedStepColumns: YardGateTrackedStepColumn[];
  isBusy: boolean;
  isLoading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  pagination?: Partial<Pagination>;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  dockOptions?: YardGateDockOption[];
  onPrintOrder: (entry: YardGateEntry) => void;
  onTableInstance?: (table: Table<YardGateEntry>) => void;
  onStatusChange: (
    type: YardGateEntry['type'],
    sourceId: number,
    status: YardGateMutableStatus
  ) => void;
  onUpdateDocks?: (entry: YardGateEntry, docks: number[]) => Promise<boolean>;
  onUpdatePhone?: (
    entry: YardGateEntry,
    phone: string | null
  ) => Promise<boolean>;
  onAddObservation?: (entry: YardGateEntry) => void;
  onManageChecklist?: (entry: YardGateEntry) => void;
  onStartJourney?: (entry: YardGateEntry) => void;
}

interface TooltipContent {
  title: string;
  description?: string;
}

const getTooltipContent = (entry: YardGateEntry): TooltipContent | null => {
  if (entry.status === 'CANCELLED') {
    return {
      title: 'Cancelado',
      description: entry.cancellationReason?.name,
    };
  }
  if (entry.status === 'NO_SHOW') {
    return { title: 'No Show' };
  }
  return null;
};

export const EntriesTable = ({
  entries,
  trackedStepColumns,
  isBusy,
  isLoading,
  pageSize = 10,
  pageSizeOptions,
  pagination,
  manualPagination,
  onPaginationChange,
  manualSorting,
  sorting,
  onSortingChange,
  dockOptions = [],
  onPrintOrder,
  onTableInstance,
  onStatusChange,
  onUpdateDocks = async () => false,
  onUpdatePhone = async () => false,
  onAddObservation = () => undefined,
  onManageChecklist = () => undefined,
  onStartJourney = () => undefined,
}: EntriesTableProps) => {
  const [tooltip, setTooltip] = useState<
    (TooltipContent & { x: number; y: number }) | null
  >(null);

  const handleRowMouseMove = (
    entry: YardGateEntry,
    event: ReactMouseEvent<HTMLTableRowElement>
  ) => {
    const content = getTooltipContent(entry);
    if (!content) {
      setTooltip(null);
      return;
    }
    setTooltip({ ...content, x: event.clientX, y: event.clientY });
  };

  const handleRowMouseLeave = () => setTooltip(null);

  const columns = useMemo(
    () =>
      buildYardGateColumns({
        trackedStepColumns,
        isBusy,
        dockOptions,
        onPrintOrder,
        onStatusChange,
        onUpdateDocks,
        onUpdatePhone,
        onAddObservation,
        onManageChecklist,
        onStartJourney,
      }),
    [
      trackedStepColumns,
      isBusy,
      dockOptions,
      onPrintOrder,
      onStatusChange,
      onUpdateDocks,
      onUpdatePhone,
      onAddObservation,
      onManageChecklist,
      onStartJourney,
    ]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={entries}
        isLoading={isLoading}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pagination={pagination}
        manualPagination={manualPagination}
        onPaginationChange={onPaginationChange}
        manualSorting={manualSorting}
        sorting={sorting}
        onSortingChange={onSortingChange}
        showControlColumns={false}
        onTableInstance={onTableInstance}
        resizable
        enableColumnPinning
        defaultColumnPinning={YARD_GATE_DEFAULT_COLUMN_PINNING}
        columnPinningVersion={1}
        storageKey="yard-gate-entries"
        noWrapHeaders
        noWrapCells
        denseRows
        highlightRowOnHover
        tableClassName="min-w-[1500px]"
        appearance="project-list"
        alwaysShowPagination
        stickyHeader
        maxHeight="max(520px, calc(100vh - 320px))"
        rowColor={(entry) =>
          entry.dockAttentionRequired ? `${Colors.Peach}` : 'transparent'
        }
        rowClassName={(entry) =>
          entry.dockAttentionRequired ? 'doca-attention' : undefined
        }
        onRowMouseEnter={handleRowMouseMove}
        onRowMouseMove={handleRowMouseMove}
        onRowMouseLeave={handleRowMouseLeave}
      />
      {tooltip ? (
        <MouseTooltip
          x={tooltip.x}
          y={tooltip.y}
          title={tooltip.title}
          description={tooltip.description}
        />
      ) : null}
    </>
  );
};
